import React from 'react';
import { SITE_CONFIG, trackEvent } from '../config/site';
import { Sparkles, ArrowRight, BookOpen, Layers, CheckCircle2, ShieldCheck, Terminal, Download, FileText } from 'lucide-react';

interface HeroProps {
  onOpenWaitlist: () => void;
}

export const Hero: React.FC<HeroProps> = ({ onOpenWaitlist }) => {
  const handlePrimaryCta = () => {
    trackEvent('hero_cta', { cta: 'solicitar_acesso_hero' });
    onOpenWaitlist();
  };

  return (
    <section className="relative pt-12 pb-20 md:pt-20 md:pb-32 overflow-hidden">
      {/* Background Radial Glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-brand-primary/15 rounded-full blur-[140px] pointer-events-none -z-10" />
      <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-brand-cyan/10 rounded-full blur-[120px] pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-brand-surface border border-brand-border text-xs font-semibold text-brand-textMuted mb-8 shadow-inner hover:border-brand-primary/40 transition-colors">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white font-medium">{SITE_CONFIG.statusBadge}</span>
            <span className="text-brand-textSubtle">•</span>
            <span className="text-brand-cyan">Vagas Controladas</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.1] mb-6">
            Engenharia de Infoprodutos <br className="hidden sm:inline" />
            <span className="text-gradient-brand">Assistida por Inteligência Artificial</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-brand-textMuted max-w-3xl mx-auto mb-10 leading-relaxed font-normal">
            O InfoForge não é um chat ou editor isolado. É uma plataforma de engenharia que transforma conhecimento e pesquisa em produtos digitais completos: <strong className="text-white font-medium">ebooks diagramados (PDF e EPUB)</strong>, cadernos de exercícios, roteiros de VSL e copywriting de conversão com consistência ponta a ponta.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
            <button
              onClick={handlePrimaryCta}
              className="w-full sm:w-auto px-8 py-4 rounded-xl text-base font-semibold text-white bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-primary bg-[length:200%_auto] hover:bg-right transition-all duration-500 shadow-xl shadow-brand-primary/25 hover:shadow-brand-primary/40 flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Sparkles className="w-5 h-5 text-brand-cyan" />
              Solicitar Acesso ao Private Beta
              <ArrowRight className="w-5 h-5" />
            </button>
            <a
              href="#como-funciona"
              className="w-full sm:w-auto px-7 py-4 rounded-xl text-base font-medium text-brand-text hover:text-white bg-brand-surface/80 hover:bg-brand-surface border border-brand-border hover:border-brand-borderHover transition-all flex items-center justify-center gap-2"
            >
              Ver Como Funciona
            </a>
          </div>

          {/* Trust Pillars */}
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-xs text-brand-textSubtle pt-2 border-t border-brand-border/60 max-w-3xl mx-auto">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>Outputs físicos (PDF, EPUB, ZIP)</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-brand-cyan" />
              <span>Verificação de consistência</span>
            </div>
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-brand-primary" />
              <span>Zero dados inventados</span>
            </div>
          </div>
        </div>

        {/* Dashboard Preview / Pipeline Mockup */}
        <div className="mt-14 relative mx-auto max-w-5xl">
          <div className="rounded-2xl p-1 bg-gradient-to-b from-brand-borderHover to-transparent shadow-2xl">
            <div className="rounded-[14px] bg-brand-surface border border-brand-border/80 overflow-hidden">
              {/* Window Header */}
              <div className="h-11 bg-brand-bg/90 px-4 flex items-center justify-between border-b border-brand-border/80 text-xs text-brand-textSubtle">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-rose-500/80" />
                  <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                  <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                  <span className="ml-3 font-mono text-[11px] text-brand-textMuted flex items-center gap-1.5">
                    <Terminal className="w-3.5 h-3.5 text-brand-cyan" />
                    infoforge-pipeline · workspace
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="hidden sm:inline-block px-2.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-medium text-[11px] border border-emerald-500/20">
                    Ambiente Certificado
                  </span>
                </div>
              </div>

              {/* Window Body Mockup */}
              <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 bg-brand-bg/60">
                {/* Left Col: Pipeline Stepper */}
                <div className="lg:col-span-4 space-y-3">
                  <div className="text-xs font-semibold text-brand-textSubtle uppercase tracking-wider mb-2">
                    Linhagem Editorial
                  </div>

                  <div className="p-3 rounded-xl bg-brand-surface border border-brand-border/60 flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-brand-primary/20 text-brand-primary flex items-center justify-center font-bold text-xs">
                      01
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white truncate">Inteligência & Tese</div>
                      <div className="text-[10px] text-brand-textSubtle">Validação de demanda & público</div>
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  </div>

                  <div className="p-3 rounded-xl bg-brand-surface border border-brand-border/60 flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-brand-secondary/20 text-brand-secondary flex items-center justify-center font-bold text-xs">
                      02
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white truncate">Arquitetura Editorial</div>
                      <div className="text-[10px] text-brand-textSubtle">Estrutura curricular & capítulos</div>
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  </div>

                  <div className="p-3 rounded-xl bg-brand-primary/10 border border-brand-primary/40 flex items-center gap-3 shadow-inner">
                    <div className="w-7 h-7 rounded-lg bg-brand-primary text-white flex items-center justify-center font-bold text-xs">
                      03
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white truncate">Compilação Física</div>
                      <div className="text-[10px] text-brand-cyan">PDF, EPUB, Workbook & Scripts</div>
                    </div>
                    <div className="w-2 h-2 rounded-full bg-brand-cyan animate-ping shrink-0" />
                  </div>

                  <div className="p-3 rounded-xl bg-brand-surface/40 border border-brand-border/40 flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-brand-surfaceLight text-brand-textSubtle flex items-center justify-center font-bold text-xs">
                      04
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <div className="text-xs font-semibold text-brand-textMuted truncate">Pacote de Campanha</div>
                      <div className="text-[10px] text-brand-textSubtle">VSL, Emails & Carrosséis</div>
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-brand-textSubtle shrink-0" />
                  </div>
                </div>

                {/* Right Col: Output Preview */}
                <div className="lg:col-span-8 rounded-xl bg-brand-surface border border-brand-border p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between pb-4 border-b border-brand-border mb-4">
                      <div>
                        <span className="text-[11px] font-medium text-brand-cyan uppercase tracking-wider">Produto em Geração</span>
                        <h4 className="text-base font-bold text-white">Automação Industrial Avançada</h4>
                        <p className="text-xs text-brand-textSubtle">Nicho: Engenharia de Controle & Sistemas Ciberfísicos</p>
                      </div>
                      <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold border border-emerald-500/20">
                        100% Consistente
                      </span>
                    </div>

                    {/* Output Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-4">
                      <div className="p-3 rounded-lg bg-brand-bg/80 border border-brand-border/60 hover:border-brand-primary/40 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <BookOpen className="w-4 h-4 text-brand-primary" />
                          <span className="text-[10px] font-mono text-emerald-400">Pronto</span>
                        </div>
                        <div className="text-xs font-semibold text-white">Ebook Master</div>
                        <div className="text-[10px] text-brand-textSubtle">PDF Diagramado + EPUB</div>
                      </div>

                      <div className="p-3 rounded-lg bg-brand-bg/80 border border-brand-border/60 hover:border-brand-primary/40 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <FileText className="w-4 h-4 text-brand-cyan" />
                          <span className="text-[10px] font-mono text-emerald-400">Pronto</span>
                        </div>
                        <div className="text-xs font-semibold text-white">Workbook</div>
                        <div className="text-[10px] text-brand-textSubtle">Exercícios de Fixação</div>
                      </div>

                      <div className="p-3 rounded-lg bg-brand-bg/80 border border-brand-border/60 hover:border-brand-primary/40 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <Layers className="w-4 h-4 text-brand-secondary" />
                          <span className="text-[10px] font-mono text-emerald-400">Pronto</span>
                        </div>
                        <div className="text-xs font-semibold text-white">VSL & Copy</div>
                        <div className="text-[10px] text-brand-textSubtle">Roteiro de 3 Atos</div>
                      </div>
                    </div>
                  </div>

                  {/* Delivery Bar */}
                  <div className="mt-4 pt-4 border-t border-brand-border flex items-center justify-between text-xs">
                    <span className="text-brand-textSubtle flex items-center gap-1.5">
                      <Download className="w-3.5 h-3.5 text-brand-cyan" />
                      Pacote consolidado: <strong className="text-white font-mono">automação-industrial-v1.zip</strong>
                    </span>
                    <span className="text-[11px] font-mono text-brand-textMuted">SHA-256 Verificado</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
